import { z } from 'zod';
/**
 * The only grant this endpoint implements. Named rather than free-form so an
 * unsupported grant fails validation with a clear message instead of being
 * silently treated as the default.
 */
export const V1_ACCESS_KEY_GRANT_TYPE = 'access_key';
/**
 * `POST /api/v1/oauth/token` request (KIR-3747, Path A).
 *
 * Shaped like OAuth 2.0 client credentials — snake_case field names included —
 * so a customer can point a stock OAuth client at it. Both fields are optional
 * because the key may instead arrive as `Authorization: Bearer <access_key>`,
 * which is what SIEM connectors that only know how to send a static header do.
 */
export const v1OAuthTokenRequestSchema = z
    .object({
    grant_type: z
        .literal(V1_ACCESS_KEY_GRANT_TYPE)
        .optional()
        .describe('The only grant this endpoint implements. Optional, since the key may instead arrive in the Authorization header.'),
    /**
     * `.trim()` before `.min(1)` so the value the handler sees is normalised at
     * the validation boundary. The header fallback is trimmed too, and both feed
     * the same Kirin-key guard — a pasted key with a stray newline must not slip
     * past a `startsWith` check and reach Descope.
     */
    access_key: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Your management API key, from Settings → API Keys. Omit it here if you are sending it as `Authorization: Bearer <key>` instead.'),
})
    .meta({
    id: 'V1OAuthTokenRequest',
    title: 'Token request',
    description: 'The body of a token exchange. Optional in full: a client that sends its key as `Authorization: Bearer <key>` sends no body at all.',
    examples: [{ grant_type: V1_ACCESS_KEY_GRANT_TYPE, access_key: '<key>' }],
});
/**
 * What the route actually validates against.
 *
 * A client sending the key in the `Authorization` header sends no body at all,
 * and Fastify surfaces that as `null` — which the object schema alone would
 * reject with a 400 before the handler ever looked at the header. Kept separate
 * from {@link v1OAuthTokenRequestSchema} so the documented request shape stays
 * an object.
 */
export const v1OAuthTokenRequestBodySchema = v1OAuthTokenRequestSchema.nullable();
/**
 * `POST /api/v1/oauth/token` response.
 *
 * `expires_in` is seconds from now, per OAuth 2.0 — not an absolute timestamp.
 * There is no refresh token: the access key itself is the long-lived credential,
 * and a client re-exchanges it whenever this token expires.
 */
export const v1OAuthTokenResponseSchema = z
    .object({
    access_token: z
        .string()
        .describe('Send as `Authorization: Bearer <access_token>` on subsequent calls.'),
    token_type: z.literal('Bearer').describe('Always `Bearer`.'),
    expires_in: z
        .number()
        .int()
        .describe('Lifetime in seconds from now — not an absolute timestamp. Re-exchange your key when it elapses; there is no refresh token, because the key is the long-lived credential.'),
})
    .meta({
    id: 'V1OAuthToken',
    title: 'Access token',
    description: 'A short-lived bearer token minted from a management API key.',
    examples: [
        {
            // A shape, not a specimen. A realistic-looking JWT here reads as a
            // credential to a secret scanner (and to a reader skimming the docs),
            // so the example says what the field is instead of imitating one.
            access_token: '<jwt>',
            token_type: 'Bearer',
            expires_in: 3600,
        },
    ],
});
//# sourceMappingURL=oauth.js.map